import { useEffect, useMemo, useState } from "react";
import { Card, CardHeader, Container, Row } from "reactstrap";
import "nouislider/distribute/nouislider.css";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DeleteModal from "../../../Components/Common/DeleteModal";
import TableContainer from "../../../Components/Common/TableContainer";
import {
  useArticles,
  useArticleCategories,
  useDeleteArticle,
} from "../../../hooks/useArticles";
import { Link } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import { useUsersList } from "../../../hooks/useUsersList";
import { getStoredAuthUser } from "../../../utils/authUser";

const getDisplayName = (user) => {
  if (!user) {
    return "-";
  }

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  return user.username || fullName || "-";
};

const getCommentCount = (comments = []) =>
  comments.reduce((total, comment) => {
    const replies = Array.isArray(comment?.replies) ? comment.replies.length : 0;
    return total + 1 + replies;
  }, 0);

const ARTICLE_IMAGE_FALLBACK =
  "https://prod.leconseillerdubtp.com/medias/default/article.jpg";

const ArticleThumbnail = ({ src, alt }) => {
  const [failedPrimary, setFailedPrimary] = useState(false);
  const [failedFallback, setFailedFallback] = useState(false);

  useEffect(() => {
    setFailedPrimary(false);
    setFailedFallback(false);
  }, [src]);

  const canTryPrimary = Boolean(src) && !failedPrimary;
  const canTryFallback = !failedFallback;
  const imageSrc = canTryPrimary
    ? src
    : canTryFallback
      ? ARTICLE_IMAGE_FALLBACK
      : null;

  const handleError = () => {
    if (canTryPrimary && src !== ARTICLE_IMAGE_FALLBACK) {
      setFailedPrimary(true);
      return;
    }
    setFailedFallback(true);
  };

  if (!imageSrc) {
    return (
      <div
        className="avatar-sm rounded d-flex align-items-center justify-content-center bg-light text-muted"
        aria-label={alt}
      >
        <i className="ri-image-line"></i>
      </div>
    );
  }

  return (
    <img src={imageSrc} alt={alt} className="avatar-sm rounded" onError={handleError} />
  );
};

const Articles = (props) => {
  const authUser = getStoredAuthUser();
  const roles = authUser?.role || [];
  const canValidateArticles = roles.some((role) => role !== "assistant");
  const [selectedFilter, setSelectedFilter] = useState("Tous");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const statusParam = useMemo(() => {
    if (selectedFilter === "Validés") return "validated";
    if (selectedFilter === "En attente") return "pending";
    return "all";
  }, [selectedFilter]);

  const {
    data: articles = [],
    isLoading: isArticlesLoading,
    isFetching: isArticlesFetching,
    error,
  } = useArticles({
    status: statusParam,
    search: debouncedSearch,
  });
  const { data: categoryList = [] } = useArticleCategories();
  const { data: users = [] } = useUsersList();
  const deleteArticleMutation = useDeleteArticle();
  const [article, setArticle] = useState(null);

  const checkedAll = () => {
    const checkall = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".entrepriseCheckBox");

    if (checkall.checked) {
      ele.forEach((ele) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele) => {
        ele.checked = false;
      });
    }
  };

  const [deleteModal, setDeleteModal] = useState(false);

  const onClickDelete = (articleItem) => {
    setArticle(articleItem);
    setDeleteModal(true);
  };

  const handleDeleteProduct = () => {
    if (article && article.id) {
      deleteArticleMutation.mutate(article.id, {
        onSuccess: () => {
          setDeleteModal(false);
        },
      });
    }
  };

  const categoriesById = useMemo(() => {
    const map = new Map();
    categoryList.forEach((category) => {
      map.set(category.id, category.designation);
    });
    return map;
  }, [categoryList]);

  const usersById = useMemo(() => {
    const map = new Map();
    users.forEach((user) => {
      map.set(user.id, user);
    });
    return map;
  }, [users]);

  const columns = useMemo(
    () => [
      {
        Header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            onClick={() => checkedAll()}
          />
        ),
        Cell: (cellProps) => {
          return (
            <input
              type="checkbox"
              className="entrepriseCheckBox"
              value={cellProps.row.original.id}
            />
          );
        },
        id: "#",
      },
      {
        Header: props.t("ILLUSTRATION"),
        Cell: (article) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <ArticleThumbnail
                src={article.row.original.article_pic}
                alt={props.t("Illustration article")}
              />
            </div>
          </div>
        ),
      },
      {
        Header: props.t("TITRE"),
        accessor: "title",
        Cell: (article) => (
          <div
            className="d-flex align-items-center"
            style={{
              width: 200,
              height: 50,
              overflow: "clip",
              verticalAlign: "middle",
              textTransform: "uppercase",
            }}
          >
            <div className="flex-grow-1">
              <h5 className="fs-14 mb-1 text-dark">
                <Link
                  to={`articles-details/${article.row.original.id}/`}
                  style={{ fontWeight: "bold", color: "#000000" }}
                >
                  {article.row.original.title}
                </Link>
              </h5>
            </div>
          </div>
        ),
      },
      {
        Header: props.t("CATEGORIE"),
        accessor: "category",
        Cell: (article) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <h5 className="fs-14 mb-1 ">
                <span className="fs-12 uppercase badge badge-soft-warning">
                  {article.row.original.category_name ||
                    categoriesById.get(article.row.original.category) ||
                    "-"}
                </span>
              </h5>
            </div>
          </div>
        ),
      },
      {
        Header: props.t("PARAGRAPHES"),
        accessor: "paragraphs",
        Cell: (article) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <h5 className="fs-14 mb-1">
                {article.row.original.paragraphs?.length || 0}
              </h5>
            </div>
          </div>
        ),
      },
      {
        Header: props.t("COMMENTAIRES"),
        Cell: (article) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <h5 className="fs-14 mb-1">
                <span
                  className="d-flex align-items-center"
                  style={{ color: "#F6BE1E" }}
                >
                  <i
                    style={{ fontSize: "15px", color: "#F6BE1E" }}
                    className="ri-message-2-line align-bottom"
                  ></i>
                  {"  "}
                  {getCommentCount(article.row.original.comments)}
                </span>
              </h5>
            </div>
          </div>
        ),
      },
      {
        Header: props.t("LIKES"),
        Cell: (article) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <h5 className="fs-14 mb-1">
                <span
                  className="d-flex align-items-center"
                  style={{ color: "#F6BE1E" }}
                >
                  <i
                    style={{ fontSize: "15px", color: "#F6BE1E" }}
                    className="ri-heart-line align-bottom"
                  ></i>
                  {"  "}
                  {article.row.original.likes_count || 0}
                </span>
              </h5>
            </div>
          </div>
        ),
      },
      {
        Header: props.t("STATUT"),
        accessor: "validated",
        Cell: (article) => (
          <>
            {canValidateArticles ? (
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h5 className="fs-14 p-2">
                    <span className="mt-4">
                      {article.row.original.validated === false ? (
                        <div
                          className="btn btn-soft-warning"
                          style={{ fontSize: "13px" }}
                        >
                          <Link
                            to={`articles-details/${article.row.original.id}/`}
                          >
                            Valider
                          </Link>
                        </div>
                      ) : (
                        <div
                          className="fs-12 uppercase badge badge-soft-success"
                          style={{ textAlign: "center", fontSize: "13px" }}
                        >
                          Validé
                        </div>
                      )}
                    </span>
                  </h5>
                </div>
              </div>
            ) : (
              "-"
            )}
          </>
        ),
      },
      {
        Header: props.t("AUTEUR"),
        accessor: "author",
        Cell: (article) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <h5 className="fs-14 mb-1 ">
                <span className="p-2 align-items-center" style={{ fontStyle: "italic" }}>
                  {getDisplayName(
                    article.row.original.author ||
                      usersById.get(article.row.original.author_id)
                  )}
                </span>
              </h5>
            </div>
          </div>
        ),
      },
      {
        Header: props.t("VALIDE PAR"),
        accessor: "validated_by",
        Cell: (article) => (
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <h5 className="fs-14 mb-1 ">
                <span className="p-2 align-items-center" style={{ fontStyle: "italic" }}>
                  {getDisplayName(
                    article.row.original.validated_by ||
                      usersById.get(article.row.original.validated_by_id)
                  )}
                </span>
              </h5>
            </div>
          </div>
        ),
      },
      {
        Header: "ACTIONS",
        Cell: (article) => {
          const articleData = article.row.original;
          return (
            <div>
              <Link
                to={`/articles-details/${articleData.id}/`}
                className="text-success d-inline-block me-2"
              >
                <i className="ri-eye-fill align-bottom"></i>
              </Link>
              <Link
                to={`/articles-update/${articleData.id}/`}
                className="text-warning d-inline-block me-2"
              >
                <i className="ri-pencil-fill align-bottom"></i>
              </Link>
              <Link
                to="#"
                className="text-danger d-inline-block"
                onClick={() => onClickDelete(articleData)}
              >
                <i className="ri-delete-bin-5-line align-middle text-danger"></i>
              </Link>
            </div>
          );
        },
      },
    ],
    [canValidateArticles, categoriesById, props, usersById]
  );

  document.title = props.t("Liste des articles");

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteProduct}
        onCloseClick={() => setDeleteModal(false)}
      />
      <Container fluid>
        <BreadCrumb
          title={props.t("Liste des articles")}
          pageTitle={props.t("Listes des articles")}
        />

        <Row>
          <Card id="orderList">
            <CardHeader className="card-header border-0">
              <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                <button className="btn btn-info add-btn">
                  <Link to={`/articles-add`} className="text-white d-inline-block">
                    <i className="ri-add-fill me-1 align-bottom" />{" "}
                    {props.t("Ajouter un article")}
                  </Link>
                </button>
                <div
                  className="d-flex align-items-center justify-content-end"
                  style={{ gap: "12px", minWidth: "520px", flexWrap: "nowrap" }}
                >
                  <label className="mb-0 fw-semibold">{props.t("Statut")}</label>
                  <select
                    className="form-select"
                    style={{ width: "170px", flex: "0 0 auto" }}
                    value={selectedFilter}
                    onChange={(event) => setSelectedFilter(event.target.value)}
                  >
                    <option value="Tous">{props.t("Tous")}</option>
                    <option value="Validés">{props.t("Validés")}</option>
                    <option value="En attente">{props.t("En attente")}</option>
                  </select>
                  <div
                    className="search-box"
                    style={{ width: "320px", flex: "0 1 320px" }}
                  >
                    <input
                      type="text"
                      className="form-control"
                      placeholder={props.t("Rechercher un article...")}
                      value={searchInput}
                      onChange={(event) => setSearchInput(event.target.value)}
                    />
                    <i className="bx bx-search-alt search-icon"></i>
                  </div>
                </div>
              </div>
            </CardHeader>
          </Card>
          <div className="col-xl-12 col-lg-12">
            <div>
              <div className="card">
                <div className="card-body pt-0">
                  {isArticlesLoading ? (
                    <div className="py-4 text-center">
                      <div>
                        <lord-icon
                          src="https://cdn.lordicon.com/xjovhxra.json"
                          trigger="loop"
                          colors="primary:#0960b6,secondary:#02376b"
                          style={{ width: "72px", height: "72px" }}
                        ></lord-icon>
                      </div>
                      <div className="mt-4">
                        <h5>{props.t("Chargement")}...</h5>
                      </div>
                    </div>
                  ) : error ? (
                    <div className="py-4 text-center">
                      <div className="mt-4">
                        <h5>{error.message || props.t("Une erreur est survenue")}</h5>
                      </div>
                    </div>
                  ) : articles.length > 0 ? (
                    <div>
                      {isArticlesFetching ? (
                        <div className="text-muted small mb-2">
                          {props.t("Mise a jour des resultats...")}
                        </div>
                      ) : null}
                      <TableContainer
                        columns={columns}
                        data={articles}
                        isGlobalFilter={false}
                        isAddUserList={false}
                        customPageSize={10}
                        divClass="table-responsive mb-1"
                        tableClass="mb-0 align-middle "
                        theadClass="table-light text-muted"
                        isProductsFilter={false}
                      />
                    </div>
                  ) : (
                    <div className="py-4 text-center">
                      <div className="mt-4">
                        <h5>{props.t("Désolé, pas de resultats trouvés")}</h5>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};
Articles.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};
export default withRouter(withTranslation()(Articles));
